import * as React from "react";
import { TripWithTenant } from "../../../../types";

interface TripWithPlacement extends TripWithTenant {
  hangar_id?: string | null;
  ramp_id?: string | null;
  hangar_name?: string | null;
  ramp_name?: string | null;
  location_name?: string | null;
  location_type?: string | null;
}

interface UseTripsTableDataOptions {
  trips: TripWithPlacement[];
  filterText: string;
  locationFilterId?: string;
  showPastTrips: boolean;
  currentTime: Date;
  startDate?: Date | null;
  endDate?: Date | null;
}

/**
 * Filters and sorts trips for display in a flat table
 */
export const useTripsTableData = ({
  trips,
  filterText,
  locationFilterId,
  showPastTrips,
  currentTime,
  startDate,
  endDate,
}: UseTripsTableDataOptions) => {
  const getTripStatus = (
    trip: TripWithPlacement
  ): "upcoming" | "active" | "past" => {
    if (!trip.arrival_date || !trip.departure_date) return "upcoming";

    const start = new Date(trip.arrival_date);
    const end = new Date(trip.departure_date);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) return "upcoming";

    if (currentTime < start) return "upcoming";
    if (currentTime > end) return "past";
    return "active";
  };

  const tableData = React.useMemo(() => {
    // Filter trips
    const filtered = trips.filter((trip) => {
      // Filter by search text
      if (filterText) {
        const searchLower = filterText.toLowerCase();
        const matchesSearch =
          trip.tail_number?.toLowerCase().includes(searchLower) ||
          trip.owner_manager?.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Filter by location (show trips assigned to this location)
      if (locationFilterId) {
        const matchesLocation =
          trip.hangar_id === locationFilterId ||
          trip.ramp_id === locationFilterId;
        if (!matchesLocation) return false;
      }

      // Filter by start date (trips arriving on or after start date)
      if (startDate && trip.arrival_date) {
        const arrivalDate = new Date(trip.arrival_date);
        if (!isNaN(arrivalDate.getTime()) && arrivalDate < startDate) {
          return false;
        }
      }

      // Filter by end date (trips departing on or before end date)
      if (endDate && trip.departure_date) {
        const departureDate = new Date(trip.departure_date);
        if (!isNaN(departureDate.getTime()) && departureDate > endDate) {
          return false;
        }
      }

      // Filter out past trips if needed
      if (!showPastTrips && getTripStatus(trip) === "past") {
        return false;
      }

      return true;
    });

    // Sort by arrival date (earliest first)
    return filtered.sort((a, b) => {
      const aDate = a.arrival_date ? new Date(a.arrival_date).getTime() : 0;
      const bDate = b.arrival_date ? new Date(b.arrival_date).getTime() : 0;
      return aDate - bDate;
    });
  }, [trips, filterText, locationFilterId, showPastTrips, currentTime, startDate, endDate]);

  return tableData;
};
